/**
 * The files the export menu hands over, the same on every platform: the
 * formats, each file's name and content type, and the 3D model (STEP, STL)
 * of a room or of the level on screen. The 2D plan formats (DXF, SVG) are
 * drawn by a platform's own plan renderer, and delivering a file (a browser
 * download, the share sheet) is the platform's.
 */
import type { FloorPlanRoom } from '@kaiizen/kai-floor-plan';
import { type AssemblyRoom, buildAssemblyScene3D, RoomSceneCache } from '../assembler';
import { type PlanLayers, roomWithLayers } from '../editor-model';
import { roomToStep, roomToStl, sceneToStep, sceneToStl } from '../view3d';

export type PlanExportFormat = 'dxf' | 'svg';
export type ModelExportFormat = 'step' | 'stl';
export type ExportFormat = PlanExportFormat | ModelExportFormat;

/** The 2D plan formats and the 3D model formats, each in menu order. */
export const PLAN_EXPORT_FORMATS: PlanExportFormat[] = ['dxf', 'svg'];
export const MODEL_EXPORT_FORMATS: ModelExportFormat[] = ['step', 'stl'];
/** Every format, in menu order. */
export const EXPORT_FORMATS: ExportFormat[] = [...PLAN_EXPORT_FORMATS, ...MODEL_EXPORT_FORMATS];

const CONTENT_TYPES: Record<ExportFormat | 'json', string> = {
  dxf: 'image/vnd.dxf',
  svg: 'image/svg+xml',
  step: 'model/step',
  stl: 'model/stl',
  json: 'application/json',
};

/** A file to hand over: its name with the format's extension, its content type, and its content (binary STL as an ArrayBuffer). */
export interface ExportFile {
  name: string;
  type: string;
  content: string | ArrayBuffer;
}

/** The room editor's files, before the extension. */
export const ROOM_EXPORT_NAME = 'floor-plan';
/** The floor plan view's every-room JSON, before the extension. */
export const ROOMS_EXPORT_NAME = 'floor-plan-rooms';
/** The floor plan view's 2D and 3D files of a level, before the extension. */
export const levelExportName = (level: number): string => `floor-plan-level-${level}`;

export const exportFile = (name: string, format: ExportFormat | 'json', content: string | ArrayBuffer): ExportFile => ({
  name: `${name}.${format}`,
  type: CONTENT_TYPES[format],
  content,
});

/** The room's 3D model, showing what `layers` shows: free elements drop out the way the 3D view drops them. */
export const roomModelContent = (room: FloorPlanRoom, format: ModelExportFormat, layers: PlanLayers): string | ArrayBuffer => {
  const shown = roomWithLayers(room, layers);
  return format === 'step' ? roomToStep(shown) : roomToStl(shown);
};

/** The level's 3D model: its rooms composed into one scene as the floor plan view's 3D composes them, nothing selected, showing what `layers` shows. */
export const levelModelContent = (rooms: readonly AssemblyRoom[], format: ModelExportFormat, layers: PlanLayers): string | ArrayBuffer => {
  const scene = buildAssemblyScene3D(rooms, new RoomSceneCache(), undefined, layers);
  return format === 'step' ? sceneToStep(scene) : sceneToStl(scene);
};
